import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import featureFlags from "../../../config/featureFlags";
import {
  Home,
  BarChart,
  X,
  User,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  CreditCard,
  Shield,
  FileText,
  Info,
  HandHeart,
  Handshake,
  BookOpen,
} from "lucide-react";
import logo from "../../../assets/images/mszila-border-logo.png";

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useSelector((state) => state.merchant);
  const navigate = useNavigate();

  if (!user) return null;

  const isMerchant = user.role === "merchant";
  const isUser = user.role === "user";

  const [activeItem, setActiveItem] = useState("/dashboard");
  const [openMenus, setOpenMenus] = useState({});

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  /* ---------------- COMMON LINKS (BOTH) ---------------- */
  const commonLinks = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/", icon: Home, label: "Home" },
    { path: "/dashboard/banner-advertisement", icon: HandHeart, label: "Banner Advertisement" },
  ];

  /* ---------------- MERCHANT ONLY ---------------- */
  const merchantLinks = [
    {
      label: "Portfolio",
      icon: BarChart,
      children: [
        { path: "/dashboard/merchant-overview", label: "Overview" },
        { path: "/dashboard/merchant-catalogue", label: "Catalogue" },
        { path: "/dashboard/merchant-quickinfo", label: "Quick Info" },
      ],
    },
    { path: "/dashboard/sponsorship-profile", icon: Handshake, label: "Profile Sponsorship" },
    { path: "/dashboard/booklet-magazine", icon: BookOpen, label: "Collective Advertising" },
    { path: "/dashboard/merchant-visitingcard", icon: CreditCard, label: "Visiting Cards" },
  ];

  /* ---------------- USER ONLY ---------------- */
  const userLinks = [
     { path: "/dashboard/user-visitingcard", icon: CreditCard, label: "Visiting Cards" },
  ];

  /* ---------------- ACCOUNT (BOTH) ---------------- */
 /* ---------------- ACCOUNT (ROLE BASED) ---------------- */
const accountLinks = [
  {
    label: "Account",
    icon: User,
    children: [
      {
        path: isMerchant
          ? "/dashboard/merchant-profile"
          : "/dashboard/user-profile",
        label: "Profile",
      },
      ...((isMerchant ? featureFlags.ENABLE_MERCHANT_SUBSCRIPTION : featureFlags.ENABLE_USER_SUBSCRIPTION)
        ? [{ path: "/dashboard/merchant-subscription", label: "Subscriptions" }]
        : []),
      { path: "/dashboard/help-support", label: "Help & Support" },
      { path: "/dashboard/my-orders", label: "My Orders" },
      { path: "/dashboard/my-bookings", label: "Upcoming Bookings" },
      ...(isMerchant ? [{ path: "/dashboard/cam-orders", label: "My CAM Bookings" }] : []),
    ],
  },
];

  /* ---------------- LEGAL (BOTH) ---------------- */
  const legalLinks = [
    { path: "/dashboard/about-us", icon: Info, label: "About Us" },
    { path: "/dashboard/terms-and-conditions", icon: FileText, label: "Terms & Conditions" },
    { path: "/dashboard/privacy-policy", icon: Shield, label: "Privacy Policy" },
  ];

  const navItems = [
    ...commonLinks,
    ...(isMerchant ? merchantLinks : []),
    ...(isUser ? userLinks : []),
    ...accountLinks,
    ...legalLinks,
  ];

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? "show" : ""}`} onClick={onClose} />

      <div className={`custom-sidebar ${isOpen ? "show" : ""}`}>
        {/* HEADER */}
        <div className="sidebar-header">
          <div className="logo-section">
            <div className="logo-icon"> <a className="logo-section" href="/">
            <img
          src={logo}
         alt="MSZILA Logo"
          className="navbar-logo"
         />
       </a></div>
            <h2 className="logo-text">{isMerchant ? "MSZILA Merchant" : "MSZILA User"}</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* NAVIGATION */}
        <nav className="sidebar-nav">
          <ul className="nav-list">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem === item.path;

              // DROPDOWN
              if (item.children) {
                return (
                  <li key={item.label} className="nav-item">
                    <button className="nav-link" onClick={() => toggleMenu(item.label)}>
                      <Icon size={20} />
                      <span className="ms-2">{item.label}</span>
                      {openMenus[item.label] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>

                    {openMenus[item.label] && (
                      <div className="submenu">
                        {item.children.map((sub) => (
                          <button
                            key={sub.path}
                            className={`submenu-link ${activeItem === sub.path ? "active" : ""}`}
                            onClick={() => {
                              setActiveItem(sub.path);
                              navigate(sub.path);
                              if (window.innerWidth < 992) onClose();
                            }}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </li>
                );
              }

              // SINGLE LINK
              return (
                <li key={item.path} className="nav-item">
                  <button
                    className={`nav-link ${isActive ? "active" : ""}`}
                    onClick={() => {
                      setActiveItem(item.path);
                      navigate(item.path);
                      if (window.innerWidth < 992) onClose();
                    }}
                  >
                    {Icon && <Icon size={20} />}
                    <span className="ms-2">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* USER FOOTER */}
        <div className="user-profile">
          <div className="profile-card">
            <div className="profile-avatar">
              <User size={20} />
            </div>
            <div className="profile-info">
              <p className="profile-name">{user.fullName || "User"}</p>
              <p className="profile-role">{user.role}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
